import React from 'react';
import MoveButton from '../../../RouteActionButtons/MoveButton';
import DeleteRouteButton from '../../../RouteActionButtons/DeleteRouteButton';
import EditButton from '../../../RouteActionButtons/EditButton';
import ReoptimizeButton from '../../../RouteActionButtons/ReoptimizeButton';
import ReverseButton from '../../../RouteActionButtons/ReverseButton';
import { ButtonTypes } from '../../../../shared/ActionButton';
import { RouteProps } from '../../../../../store/mst/utils/getRoutesProps';
import DrawRouteButton from '../../../RouteActionButtons/DrawRouteButton';
import DuplicateRouteButton from '../../../RouteActionButtons/DuplicateRouteButton';
import DownloadRouteButton from '../../../RouteActionButtons/DownloadRouteButton';
import UnpublishButton from '../../../RouteActionButtons/UnpublishButton';

interface MenuItemsProps {
  routeProps: RouteProps;
  overrideLoading: boolean;
  projectId?: number;
  permissions: {
    canEditRoutes: boolean;
    canDeleteRoutes: boolean;
    canCreateOptimization: boolean;
    canAssignStopsToRoutes: boolean;
    canCreateRoutes: boolean;
    canDownloadSolution: boolean;
  };
}

const getRouteActionsMenuItems = ({
  routeProps,
  overrideLoading,
  projectId,
  permissions,
}: MenuItemsProps) => {
  const {
    canEditRoutes, canDeleteRoutes, canCreateOptimization, canAssignStopsToRoutes, canCreateRoutes, canDownloadSolution,
  } = permissions;
  const { isStarted, isDispatched, isLoading, isReversing } = routeProps;

  // Reoptimize and draw are both guarded by CREATE_OPTIMIZATION, which the registry closes on a
  // published route because they rebuild the stop sequence from scratch.
  const optimizeDisabled = isLoading || isStarted || !canCreateOptimization || isReversing;
  // Reverse is guarded by ASSIGN_STOPS_TO_ROUTES and stays open on a published route until the
  // driver starts it, since it preserves the existing stops.
  const reverseDisabled = isLoading || isStarted || !canAssignStopsToRoutes || isReversing;

  return [
    {
      key: 'edit',
      button: (
        <EditButton
          type={ButtonTypes.MENU}
          key="edit"
          routeProps={{
            ...routeProps,
            isLoading: overrideLoading,
            disabled: overrideLoading || !canEditRoutes || isReversing,
          }}
        />
      ),
    },
    {
      key: 'reoptimize',
      button: (
        <ReoptimizeButton
          type={ButtonTypes.MENU}
          key="reoptimize"
          routeProps={{
            ...routeProps,
            disabled: optimizeDisabled,
          }}
        />
      ),
    },
    {
      key: 'reverse',
      button: (
        <ReverseButton
          type={ButtonTypes.MENU}
          key="reverse"
          routeProps={{
            ...routeProps,
            disabled: reverseDisabled,
          }}
        />
      ),
    },
    {
      key: 'draw',
      button: (
        <DrawRouteButton
          type={ButtonTypes.MENU}
          key="draw"
          routeProps={{
            ...routeProps,
            disabled: optimizeDisabled,
          }}
        />
      ),
    },
    {
      key: 'move',
      button: (
        <MoveButton
          type={ButtonTypes.MENU}
          key="move"
          routeUuids={routeProps.uuids}
          disabled={isLoading || isDispatched || !canEditRoutes || isReversing}
          routeProps={routeProps}
        />
      ),
    },
    {
      key: 'duplicate',
      button: (
        <DuplicateRouteButton
          type={ButtonTypes.MENU}
          key="duplicate"
          routeUuids={routeProps.uuids}
          disabled={isLoading || !canCreateRoutes || isReversing}
        />
      ),
    },
    {
      key: 'download',
      button: (
        <DownloadRouteButton
          type={ButtonTypes.MENU}
          key="download"
          routeProps={routeProps}
          projectId={projectId!}
          disabled={!canDownloadSolution || !projectId}
        />
      ),
    },
    {
      key: 'unpublish',
      button: (
        <UnpublishButton key="unpublish" />
      ),
    },
    {
      key: 'delete',
      button: (
        <DeleteRouteButton
          type={ButtonTypes.MENU}
          key="delete"
          routeProps={{
            ...routeProps,
            disabled: isLoading || isDispatched || !canDeleteRoutes || isReversing,
          }}
        />
      ),
    },
  ];
};

export default getRouteActionsMenuItems;
